import React, { useCallback, useState } from 'react';
import { Formik, Form } from 'formik';
import { FormikControl } from '../forms/formik';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
    callVerifyHuman,
    NETLIFY_FUNCTIONS_PATH,
    CTA_CLICK_SMS_OPT_IN,
    GetEmailTemplate,
    handleSendEmail,
    getFacebookCookies,
    getOptimizelyData,
    appendFormUrlToExposures,
    syncPardotTracking,
    isLeadsCaptureResponse,
    validateWithPossibleNow,
    getLeadsCaptureEcho,
    safeFullStory as FullStory,
} from '../../utils';
import axios from 'axios';
import { ClaimInfoCard } from './CouponSuccess';
import { couponSchema } from '../../utils/formSchemas';
import { couponInitialValues } from './utils/couponInitialValues';
import { useScrollToAnchor } from '../../hooks/builder/useScrollToAnchor';
import {
    FormSuccess,
    FormFailed,
    FormSubmitting,
    AttributionSurvey,
    LocationsDropdownWrapper,
    MarketingConsentMsg,
} from '../forms/shared';

export function ClaimDiscount({ siteData, coupon, type }) {
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [showAttributionSurvey, setShowAttributionSurvey] = useState(false);
    const [formValues, setFormValues] = useState(null);
    const { scrollToAnchor } = useScrollToAnchor({ offset: 300 });

    // Create an event handler so you can call the verification on button click event or form submit
    const handleReCaptchaVerify = useCallback(async () => {
        if (!executeRecaptcha) {
            console.log('Execute recaptcha not yet available');
            return;
        }
        const user = await callVerifyHuman('discountForm', executeRecaptcha);
        return user;
        // Do whatever you want with the token
    }, [executeRecaptcha]);

    // Handle form submission
    const handleSubmit = async (values, helpers) => {
        scrollToAnchor('claim-discount-form');
        helpers.setSubmitting(true);
        const { fbp, fbc } = getFacebookCookies();
        const { experiment_exposures, experiment_user_ID } = getOptimizelyData();
        try {
            const user = await handleReCaptchaVerify();
            if (user === 'bot') {
                helpers.setErrors('bot', true);
                throw new Error('Bot detected');
            } else {
                // Validate phone and email with PossibleNow
                const { phoneFound, wireless, emailFound, result, flags } = await validateWithPossibleNow(
                    values.phone,
                    values.email
                );

                const formData = {
                    ...values,
                    updates: true, // Always true for TCPA compliance
                    marketing_sms_optout: !CTA_CLICK_SMS_OPT_IN,
                    description: `Claim Discount | CouponCode: ${coupon.fetchedData.couponCode.toUpperCase()} | Discount: ${
                        coupon.fetchedData.discount
                    }%`,
                    cloudamp__data__c: localStorage.getItem('cloudamp__data__c') || '',
                    formType: type,
                    fbp,
                    fbc,
                    experiment_exposures: appendFormUrlToExposures(experiment_exposures),
                    experiment_user_ID,
                    phoneFound,
                    wireless,
                    emailFound,
                    result,
                    flags,
                };

                const response = await axios.post(`${NETLIFY_FUNCTIONS_PATH}form-handler`, formData);

                // Store form values for the attribution survey. The handler returns submissionId
                // and TU IDs when leads-capture is on; the survey echoes them so its answer
                // updates the original lead instead of creating a second one.
                setFormValues({ ...formData, ...getLeadsCaptureEcho(response) });

                // Only sync Pardot tracking on the legacy path; leads-capture already wrote the lead.
                if (response?.status === 200 && !isLeadsCaptureResponse(response)) {
                    syncPardotTracking(values);
                }

                // Show attribution survey instead of immediate success
                setShowAttributionSurvey(true);

                helpers.setSubmitting(false);
                helpers.setStatus('Success');

                // FullStory Event Tracking
                FullStory('trackEvent', {
                    name: 'Claim Discount Form - Success',
                    properties: {
                        name: 'Discount',
                        user_email: values.email,
                        sms_optin: CTA_CLICK_SMS_OPT_IN,
                    },
                });

                const htmlTemplateForCouponEmail = GetEmailTemplate.couponCodeEmailTemplate(
                    coupon.fetchedData.discount,
                    new Date(coupon.fetchedData.expDate.toDate()).toString().slice(0, 15)
                );
                handleSendEmail(values.email, 'Your Milan Savings!', htmlTemplateForCouponEmail, 'Coupon Claim');
                helpers.resetForm();
            }
        } catch (error) {
            console.error('Form submission error:', error);
            const errorMessage = error instanceof Error ? error.message : 'Form submission error';

            helpers.setSubmitting(false);
            helpers.setStatus('Failed');
            setFormValues(null);
            setShowAttributionSurvey(false);

            // FullStory Event Tracking with enhanced error details
            FullStory('trackEvent', {
                name: 'Claim Discount Form - Error',
                properties: {
                    name: 'Claim Discount',
                    user_email: values.email,
                    error_message: errorMessage,
                    error_details: axios.isAxiosError(error)
                        ? {
                              status: error.response?.status,
                              statusText: error.response?.statusText,
                          }
                        : 'Non-axios error',
                },
            });

            // Log that Pardot tracking was skipped due to form submission failure
            FullStory('trackEvent', {
                name: 'Claim Discount Form - Pardot Sync Skipped',
                properties: {
                    user_email: values.email,
                    error_message: errorMessage,
                    reason: 'Actual Form submission failed',
                },
            });
        }
    };

    const handleAttributionComplete = () => {
        setShowAttributionSurvey(false);

        setTimeout(() => {
            setFormValues(null);
        }, 10000); // Clear form values after 10 second
    };

    return (
        <div id="claim-discount-form">
            <Formik
                initialValues={couponInitialValues}
                validationSchema={couponSchema}
                onSubmit={handleSubmit}
                validateOnChange={false}
            >
                {(formik) => {
                    if (formik.isSubmitting) {
                        return <FormSubmitting />;
                    }

                    // Handle error state
                    if (formik.status === 'Failed') {
                        return <FormFailed />;
                    }

                    if (formValues) {
                        if (showAttributionSurvey) {
                            return (
                                <AttributionSurvey
                                    formValues={formValues}
                                    onComplete={handleAttributionComplete}
                                    InfoCard={() => <ClaimInfoCard coupon={coupon} />}
                                />
                            );
                        }
                        return <FormSuccess />;
                    }

                    return (
                        <Form className="w-100 card p-3 milan-bg-white mb-5 fs-mask">
                            <h5 className="mb-4 text-capitalize milan-text-primary">Claim Your Discount</h5>
                            <div className="row mb-4 text-start">
                                <div className="col-12 col-lg-6 mb-4 mb-lg-0">
                                    <FormikControl
                                        control="input"
                                        name="first_name"
                                        placeholder="First Name"
                                        type="text"
                                    />
                                </div>
                                <div className="col-12 col-lg-6">
                                    <FormikControl
                                        control="input"
                                        name="last_name"
                                        placeholder="Last Name"
                                        type="text"
                                    />
                                </div>
                            </div>
                            <div className="mb-4 text-start">
                                <FormikControl control="emailInput" placeholder="Your Email" name="email" />
                            </div>
                            <div className="mb-4 text-start">
                                <FormikControl
                                    control="phoneInput"
                                    placeholder="Phone Number"
                                    name="phone"
                                    type="tel"
                                />
                            </div>
                            <div className="mb-2 text-start">
                                <label htmlFor="location">Location</label>
                                <LocationsDropdownWrapper {...{ siteData, formik }} fieldName="location" />
                            </div>
                            <div className="lh-1 text-start mb-3">
                                <MarketingConsentMsg
                                    cta={
                                        <>
                                            Submit <sup>*</sup>
                                        </>
                                    }
                                />
                            </div>
                            <button
                                className="milan-btn milan-red-btn w-100"
                                type="submit"
                                disabled={!formik.isValid || formik.isSubmitting}
                            >
                                {formik.isSubmitting ? (
                                    'Submitting...'
                                ) : (
                                    <>
                                        Submit <sup>*</sup>
                                    </>
                                )}
                            </button>
                        </Form>
                    );
                }}
            </Formik>
        </div>
    );
}

export default ClaimDiscount;
